import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { WINDOW } from '@ng-web-apis/common';
import { DataWindow, SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ExpressNumber,
  FundId,
  FundShareClassId,
  GlobalId,
  LiteratureDocument,
  ProductData,
  SegmentId,
  ShareClassCode,
} from '@types';
import { Logger } from '@utils/logger';
import {
  combineLatest,
  from,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
} from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Nominal } from 'simplytyped';
import { AppStateService } from './app-state.service';
import { SegmentService } from './segment.service';
import { StorageService } from './storage.service';

const logger = Logger.getLogger('PersonalisationAPIService');

// TODO: rename all "USServicing" prefixes to "Personalisation"

interface USServicingPersonalDataProductDto {
  oneTis: FundShareClassId;
  sortOrder?: number;
}

export type USServicingToken = Nominal<string, 'USServicingToken'>;

export const ID_TOKEN_PARAM = 'ft_token';

export interface USServicingPersonalDataDocuments {
  asOfDate: string;
  documentType: string;
  documentTitle: string;
  documentURL: string;
  productId: string;
  productName: string;
  updatedDate?: string;
  fileUploadDate: string;
  createdDate?: string;
  // date format coming as '2023-06-22 09:36:45.912'
  primaryShareClassCode?: ShareClassCode;
  translatedDocumentType?: string;
  isChecked?: boolean;
  MSDContactGUIDId?: string;
  documentLanguage?: string;
  friendlyDocType?: string;
  productSubscriptionId?: string;
  strategicInvestmentManager?: string;
}

export type USServicingProductRelationship = 'favorite' | 'subscribed' | 'held';

export interface USServicingFundDoc extends LiteratureDocument {
  fileLink?: string;
}

export interface USServicingFundDocs {
  fundId: FundId;
  relationships: USServicingProductRelationship[];
  shareClassCode: ShareClassCode;
  product?: ProductData;
  docs?: USServicingFundDoc[];
}

interface USServicingPersonalDataProductDto {
  oneTis: FundShareClassId;
  sortOrder?: number;
}

export interface USServicingPersonalDataDto {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneExtension?: string;
  phoneNumber?: string;
  companyName?: string;
  dealerNumber?: string;
  branchNumber?: string;
  repNumber?: string;
  ftcomRegistered?: string;
  identifiers: {
    globalId: GlobalId;
    expressNumber: ExpressNumber;
  };
  favoriteProducts?: USServicingPersonalDataProductDto[];
  subscribedProducts?: USServicingPersonalDataProductDto[];
  heldProducts?: USServicingPersonalDataProductDto[];
  // TODO: don't know what type of data below yet
  salesTeamCoverage: null;
  subscribedDocuments: USServicingPersonalDataDocuments[];
}

export interface USServicingPersonalDataErrorDto {
  error: {
    message: string;
  };
}

export interface USServicingPersonalDataProduct {
  fundId: FundId;
  shareClassCode: ShareClassCode;
  relationships: USServicingProductRelationship[];
  sortOrder?: number;
}

export interface USServicingPersonalData {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneExtension?: string;
  phoneNumber?: string;
  companyName?: string;
  dbrInfo?: {
    dealerNumber?: string;
    branchNumber?: string;
    repNumber?: string;
  };
  ftcomRegistered?: boolean;
  identifiers: {
    globalId: GlobalId;
    expressNumber: ExpressNumber;
  };
  products?: USServicingPersonalDataProduct[];
  heldProducts?: USServicingPersonalDataProduct[];
  subscribedDocuments: USServicingPersonalDataDocuments[];
}

export interface USServicingRegisterAPITokenData {
  userGroup: string;
  userId?: string;
  businessKey?: string;
}

export class PersonalisationMissingTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PersonalisationMissingTokenError';
  }
}

export class PersonalisationHttpError extends Error {
  code: number;
  constructor(message: string, code) {
    super(message);
    this.name = 'PersonalisationHttpError';
    this.code = code;
  }
}

// params determine what data we want returned.
const personalisationApiParams =
  'includeHeldProducts=true&includefavoriteProducts=true&includeSubscribedDocuments=true&includeSalesTeamCoverage=true';

@Injectable({
  providedIn: 'root',
})
export class PersonalisationAPIService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private personalData$: ReplaySubject<USServicingPersonalData> = new ReplaySubject<USServicingPersonalData>(
    1
  );
  // emit error+details instead of just boolean
  private invalidTokenError$: ReplaySubject<Error> = new ReplaySubject<Error>(
    1
  );

  private loggedInUserError$: ReplaySubject<Error> = new ReplaySubject<Error>(
    1
  );
  private profileDataSubscription$: Array<Subscription> = [];

  constructor(
    @Inject(WINDOW) readonly windowRef: DataWindow,
    private http: HttpClient,
    private storageService: StorageService,
    private segmentService: SegmentService,
    private appStateService: AppStateService,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService
  ) {
    logger.debug('PersonalisationAPIService constructor()');
    // Listen to Personalisation Data changes for FP segment only
    this.segmentService
      .getCurrentSegmentId$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((segmentId: SegmentId) => {
        if (segmentId === SegmentId.FINANCIAL_PROFESSIONALS) {
          // ProfileData is always present and set in index.html (it is required for Medallia)
          this.profileDataSubscription$.push(
            this.getGlobalId$().subscribe((globalId: GlobalId) => {
              this.windowRef.profileData.globalId = globalId;
            })
          );
          this.profileDataSubscription$.push(
            this.getExpressNumber$().subscribe(
              (expressNumber: ExpressNumber) => {
                this.windowRef.profileData.expressNumber = expressNumber;
              }
            )
          );
        } else {
          // Delete globalId, expressNumber and unsubscribe value changes for non FP users
          this.profileDataUnsubscribe();
          if (this.windowRef?.profileData?.globalId) {
            delete this.windowRef.profileData.globalId;
          }
          if (this.windowRef?.profileData?.expressNumber) {
            delete this.windowRef.profileData.expressNumber;
          }
        }
      });
  }

  // called by app module on startup
  public init(): void {
    logger.debug('PersonalisationAPIService init()');
    this.checkIfIdentified();

    // listen for token changes and get data as soon as we have one
    // NB getIdentityToken$() will emit an empty string if nothing set
    this.getIdentityToken$()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((token) => {
          if (!token) {
            // check that a token is set, and not an empty string
            // this error can be ignored, except on USServicing page where it should be logged
            this.invalidTokenError$.next(
              new PersonalisationMissingTokenError('No token set')
            );
            return false;
          }
          // only emit (and cause http request) if we have a token set
          return true;
        }),
        switchMap(
          (token: USServicingToken): Observable<USServicingPersonalData> =>
            this.getTokenPersonalData$(token)
        )
      )
      .subscribe(
        this.handlePersonalisationApiResponse,
        this.handlePersonalisationApiError
      );
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.profileDataUnsubscribe();
  }

  public callPersonalisationApiForLoggedInUser(
    token: USServicingToken,
    expressNumber: string
  ): void {
    this.getTokenPersonalData$(token, expressNumber).subscribe(
      this.handlePersonalisationApiResponse,
      this.handlePersonalisationApiError
    );
  }

  public getPersonalData$ = (): Observable<USServicingPersonalData> =>
    this.personalData$.asObservable();

  public getGlobalId$ = (): Observable<GlobalId> =>
    this.personalData$.pipe(
      map(
        (personalData: USServicingPersonalData): GlobalId =>
          personalData.identifiers?.globalId
      )
    );

  /**
   * map personalData.ftcomRegistered as Observable.
   */
  public isRegistered$ = (): Observable<boolean> => {
    return this.personalData$.pipe(
      map(
        (personalData: USServicingPersonalData): boolean =>
          personalData.ftcomRegistered
      )
    );
  };

  public getExpressNumber$ = (): Observable<ExpressNumber> =>
    this.personalData$.pipe(
      map(
        (personalData: USServicingPersonalData): ExpressNumber =>
          personalData.identifiers?.expressNumber
      )
    );

  /**
   * NB: this will initially return empty array, then array with fund and share class ids, then array with docs added
   * @returns an observable with funds and associated docs
   */
  public getFundData$ = (): Observable<USServicingFundDocs[]> =>
    this.personalData$.pipe(
      map(
        (data: USServicingPersonalData): USServicingFundDocs[] => data.products
      )
    );

  public invalidToken$ = (): Observable<Error> =>
    this.invalidTokenError$.asObservable();

  public getIdentityToken$(): Observable<USServicingToken> {
    return from(this.storageService.getIdentityToken());
  }

  /**
   * Check identified users only for FP users.
   * Service-center page is for FP only users.
   */
  public isIdentified$ = (): Observable<boolean> =>
    this.segmentService.getCurrentSegmentId$().pipe(
      takeUntil(this.unsubscribe$),
      switchMap(
        (segmentId: SegmentId): Observable<boolean> => {
          if (segmentId === SegmentId.FINANCIAL_PROFESSIONALS) {
            return this.getIdentityToken$().pipe(map((token) => !!token));
          }
          return of(false);
        }
      )
    );

  private handlePersonalisationApiResponse = (
    data: USServicingPersonalData
  ): void => {
    logger.debug('mapped personal data', data);
    datadogRum?.setGlobalContextProperty(
      'globalid',
      data?.identifiers?.globalId
    );
    this.personalData$.next(data);
    logger.debug('valid token');
    this.invalidTokenError$.next(null);
  };

  private handlePersonalisationApiError = (
    httpError: HttpErrorResponse
  ): void => {
    logger.error('PersonalisationAPI token http error:', httpError);
    // if there ia any error in personalization api, emit it
    if (httpError.error) {
      this.invalidTokenError$.next(
        new PersonalisationHttpError(httpError.message, httpError.status)
      );
      this.invalidateIdentity();
    }
    // otherwise, let error get handled elsewhere
  };

  public invalidateIdentity(): void {
    this.storageService.removeIdentyToken();
  }

  private checkIfIdentified(): void {
    const params: URLSearchParams = new URLSearchParams(
      this.windowRef.location.search
    );
    logger.debug('params before', params);
    const urlToken: USServicingToken = params.get(
      ID_TOKEN_PARAM
    ) as USServicingToken;
    if (urlToken) {
      this.storageService.setIdentityToken(urlToken);

      // assume that if token in url, then user must be an FP, so we force it
      this.segmentService.setSegment(SegmentId.FINANCIAL_PROFESSIONALS, true);

      // remove identity token from url for security
      params.delete(ID_TOKEN_PARAM);
      this.windowRef.history.replaceState(
        null,
        '',
        `${this.windowRef.location.pathname}?${params}${this.windowRef.location.hash}`
      );
    }
    logger.debug('params after', params);
  }

  /**
   * Returns all mapped documents
   * @param documents - Array of Documents
   */
  private mapSubscribedDocuments(
    documents: USServicingPersonalDataDocuments[]
  ): USServicingPersonalDataDocuments[] {
    if (documents?.length > 0) {
      return documents.map((doc: USServicingPersonalDataDocuments) => {
        return {
          ...doc,
          translatedDocumentType: this.getTranslatedDctermType(
            doc.documentType
          ),
        };
      });
    }
  }

  private getPersonalisationApiUrlForIdentityToken = (
    token: USServicingToken
  ): string =>
    `${
      this.appStateService.getPersonalisationApi()?.apiBaseUrl
    }/getClient/tokenId/${encodeURIComponent(
      token
    )}?${personalisationApiParams}`;

  private getPersonalisationApiUrlForExpressNumber = (
    expressNumber: string
  ): string =>
    `${
      this.appStateService.getPersonalisationApi()?.apiBaseUrl
    }/getClient/expressNumber/${expressNumber}?${personalisationApiParams}`;

  // NB: Angular doesn't seem to have a type for http options:
  // https://stackoverflow.com/questions/56602725/why-doesnt-angular-provide-a-type-for-httpclients-options-paramter
  private getHttpOptions = (token?: USServicingToken) => {
    if (token) {
      return {
        headers: new HttpHeaders({
          client_id: this.appStateService.getPersonalisationApi()?.clientID,
          client_secret: this.appStateService.getPersonalisationApi()
            ?.clientSecret,
          Authorization: token,
        }),
      };
    }
    return {
      headers: new HttpHeaders({
        client_id: this.appStateService.getPersonalisationApi()?.clientID,
        client_secret: this.appStateService.getPersonalisationApi()
          ?.clientSecret,
      }),
    };
  };

  public mapFavoritesFundIds = (
    rawProducts: FundShareClassId[]
  ): USServicingPersonalDataProduct[] => {
    const validFunds: Record<string, USServicingPersonalDataProduct> = {};
    for (const rawProduct of rawProducts) {
      if (rawProduct) {
        const [fundId, shareClassCode] = rawProduct.split('-');
        if (!fundId || !shareClassCode) {
          logger.warn(` Invalid oneTis code: ${rawProduct}`);
        } else if (!this.siteConfigService.isActiveShareClass(rawProduct)) {
          logger.warn(`Inactive share class: ${rawProduct}`);
        } else {
          validFunds[rawProduct] = {
            fundId: fundId as FundId,
            shareClassCode: shareClassCode as ShareClassCode,
            relationships: ['favorite'],
          };
        }
      }
    }
    return Object.values(validFunds);
  };

  private mapFundIds = (
    relationship: USServicingProductRelationship,
    globalId: GlobalId,
    validFunds: Record<string, USServicingPersonalDataProduct>,
    rawProducts: USServicingPersonalDataProductDto[]
  ) => {
    for (const rawProduct of rawProducts) {
      if (rawProduct?.oneTis) {
        const [fundId, shareClassCode] = rawProduct.oneTis.split('-');
        // check both fundId and shareClassCode are passed
        // TODO this could add more checks in future
        if (!fundId || !shareClassCode) {
          logger.warn(
            `globalId: ${globalId}. Invalid oneTis code: ${rawProduct.oneTis}`
          );
        } else if (
          !this.siteConfigService.isActiveShareClass(rawProduct.oneTis)
        ) {
          logger.warn(
            `globalId: ${globalId}. Inactive share class: ${rawProduct.oneTis}`
          );
        } else {
          validFunds[rawProduct.oneTis] = {
            fundId: fundId as FundId,
            shareClassCode: shareClassCode as ShareClassCode,
            relationships: [relationship],
            sortOrder: rawProduct?.sortOrder,
          };
        }
      }
    }
  };

  private mapPersonalData = ([rawData, isPopulated]: [
    USServicingPersonalDataDto,
    boolean
  ]): USServicingPersonalData => {
    const globalId: GlobalId = rawData.identifiers?.globalId;
    const validFunds: Record<string, USServicingPersonalDataProduct> = {};
    this.mapFundIds('held', globalId, validFunds, rawData.heldProducts || []);
    const subscribedDocuments: USServicingPersonalDataDocuments[] =
      this.mapSubscribedDocuments(rawData.subscribedDocuments) || [];

    // split out held funds only
    const validHeldFunds: Record<string, USServicingPersonalDataProduct> = {};
    this.mapFundIds(
      'held',
      globalId,
      validHeldFunds,
      rawData.heldProducts || []
    );

    return {
      name: rawData.name,
      firstName: rawData.firstName,
      lastName: rawData.lastName,
      email: rawData.email,
      phoneExtension: rawData?.phoneExtension,
      phoneNumber: rawData?.phoneNumber,
      companyName: rawData?.companyName,
      dbrInfo: {
        dealerNumber: rawData?.dealerNumber,
        branchNumber: rawData?.branchNumber,
        repNumber: rawData?.repNumber,
      },
      ftcomRegistered: rawData.ftcomRegistered === 'Y',
      identifiers: {
        globalId,
        expressNumber: rawData.identifiers?.expressNumber,
      },
      subscribedDocuments,

      products: Object.values(validFunds),
      heldProducts: Object.values(validHeldFunds),
    };
  };

  private getTranslatedDctermType(dctermsType: string): string {
    return dctermsType
      ? this.translateService.instant(`literature.${dctermsType}`)
      : undefined;
  }

  private getTokenPersonalData$ = (
    token: USServicingToken,
    expressNumber?: string // only if we have have a ping token
  ): Observable<USServicingPersonalData> => {
    const httpRequest = expressNumber // indicates we have a ping token
      ? this.sendPersonalisationApiHttpReqForPingToken(token, expressNumber)
      : this.sendPersonalisationApiHttpReqForIdentityToken(token);
    // data is combined to make sure siteConfig is loaded before next step
    return combineLatest([
      httpRequest,
      this.siteConfigService.getIsPopulated$(),
    ]).pipe(
      tap(
        ([data, isPopulated]: [USServicingPersonalDataDto, boolean]): void => {
          logger.debug('get funds for token', isPopulated, token, data);
        }
      ),
      filter(
        ([data, isPopulated]: [USServicingPersonalDataDto, boolean]): boolean =>
          isPopulated
      ),
      map(this.mapPersonalData)
    );
  };

  private sendPersonalisationApiHttpReqForIdentityToken(
    token: USServicingToken
  ) {
    return this.http.get<USServicingPersonalDataDto>(
      this.getPersonalisationApiUrlForIdentityToken(token),
      this.getHttpOptions()
    );
  }

  private sendPersonalisationApiHttpReqForPingToken(
    token: USServicingToken,
    expressNumber: string
  ) {
    return this.http.get<USServicingPersonalDataDto>(
      this.getPersonalisationApiUrlForExpressNumber(expressNumber),
      this.getHttpOptions(token)
    );
  }

  private profileDataUnsubscribe(): void {
    this.profileDataSubscription$.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
